import { CrudPage } from '../../components/CrudPage';
import { type DemandDto, DemandControllerService } from '../../api';
import DemandCrudFormDialog from '../forms/DemandCrudFormDialog';
import { useTranslation } from 'react-i18next';
import { demandEntityFields } from '../../config/entityFields';

// Type with required id for the CrudPage
type DemandWithId = DemandDto & { id: string };

const DemandsPage = () => {
  const { t, i18n } = useTranslation();
  
  // Helper function to convert DemandDto[] to DemandWithId[]
  const fetchData = async (): Promise<DemandWithId[]> => {
    const data = await DemandControllerService.findAll();

    // Null-check: Ensure data is an array
    if (!data || !Array.isArray(data)) {
      return [];
    }

    return data
      .map(item => ({
        ...item,
        id: item.uuid || ''
      }))
      .filter(item => item.id);
  };

  return (
    <CrudPage<DemandWithId>
      titleKey="headers.demands"
      gridMeta={[
        { field: 'materialName', headerKey: 'demand.grid.field.materialName', width: 150 },
        { 
          field: 'demandDate', 
          headerKey: 'demand.grid.field.demandDate', 
          width: 120,
          render: (data) => {
            if (!data.demandDate) return '';
            const date = new Date(data.demandDate);
            return date.toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric' 
            });
          }
        },
        { 
          field: 'dueTime', 
          headerKey: 'demand.grid.field.dueTime', 
          width: 100,
          render: (data) => {
            if (!data.dueTime) return '-';
            return data.dueTime.substring(0, 5); // Show only HH:mm
          }
        },
        { field: 'quantity', headerKey: 'demand.grid.field.quantity', width: 100, type: 'number', align: 'Right' },
        {
          field: 'demandType',
          headerKey: 'demand.grid.field.demandType',
          width: 100,
          render: (data) => {
            if (!data.demandType) return '';
            return data.demandType === 'ORDER' ? t('forms.demand.sourceOrder') : t('forms.demand.sourceForecast');
          }
        },
        { field: 'customerOrderNumber', headerKey: 'demand.grid.field.customerOrderNumber', width: 150 }
      ]}
      fetchData={fetchData}
      saveData={async (data: DemandWithId) => {
          // Convert back to DemandDto format
          const apiData: DemandDto = {
            ...data,
            uuid: data.id || undefined
          };

          if (data.id) {
            await DemandControllerService.update(data.id, apiData);
          } else {
            await DemandControllerService.create(apiData);
          }
      }}
      deleteData={(id: string) => DemandControllerService.delete(id)}
      FormComponent={DemandCrudFormDialog}
      showImportButton={true}
      importEntityType="demands"
      onImportData={async (data: DemandWithId[]) => {
        // Bulk import demands
        for (const demand of data) {
          try {
            const apiData: DemandDto = {
              ...demand,
              uuid: demand.id || undefined
            };
            
            if (demand.id) {
              await DemandControllerService.update(demand.id, apiData);
            } else {
              await DemandControllerService.create(apiData);
            }
          } catch (error) {
            console.error('Error importing demand:', error);
            throw error;
          }
        }
      }}
      entityFields={demandEntityFields}
      entityName="demand"
      importOptions={{
        allowPartialImport: true,
        batchSize: 25,
        validateBeforeImport: true,
        showDetailedErrors: true,
        deferredSave: false
      }}
    />
  );
};

export default DemandsPage;
